/**
 * Generic SDK Loader Interface and Implementation
 * Provides a reusable SDK loading mechanism
 */

export interface ISDKLoader {
  isLoaded(): boolean;
  isLoading(): boolean;
  getError(): string | null;
  loadSDK(): Promise<void>;
  reset(): void;
}

/**
 * SDK loading state
 */
interface SDKLoadState {
  loaded: boolean;
  loading: boolean;
  error: string | null;
}

/**
 * Generic SDK loader configuration
 */
export interface SDKLoaderConfig {
  sdkUrl: string;
  globalObjectName: string;
  loadTimeout?: number;
}

/**
 * Generic SDK loader class for managing external SDK loading
 */
export class GenericSDKLoader implements ISDKLoader {
  private static instances: Map<string, GenericSDKLoader> = new Map();
  private static globalLoadPromise: Promise<void> | null = null;
  
  private config: SDKLoaderConfig;
  private state: SDKLoadState = {
    loaded: false,
    loading: false,
    error: null
  };
  private loadPromise: Promise<void> | null = null;

  /**
   * Get or create singleton instance for a specific SDK
   */
  static getInstance(config: SDKLoaderConfig): GenericSDKLoader {
    const key = `${config.sdkUrl}-${config.globalObjectName}`;
    
    if (!GenericSDKLoader.instances.has(key)) {
      GenericSDKLoader.instances.set(key, new GenericSDKLoader(config));
    }
    
    return GenericSDKLoader.instances.get(key)!;
  }

  /**
   * Preload SDK globally (call once at app startup)
   * This ensures SDK is ready before any connections are needed
   */
  static async preloadGlobalSDK(config: SDKLoaderConfig): Promise<void> {
    if (GenericSDKLoader.globalLoadPromise) {
      return GenericSDKLoader.globalLoadPromise;
    }

    const loader = GenericSDKLoader.getInstance(config);
    GenericSDKLoader.globalLoadPromise = loader.loadSDK();
    
    try {
      await GenericSDKLoader.globalLoadPromise;
    } catch (error) {
      GenericSDKLoader.globalLoadPromise = null;
      throw error;
    }
    
    return GenericSDKLoader.globalLoadPromise;
  }

  /**
   * Check if SDK is globally available (faster than instance check)
   */
  static isGloballyLoaded(globalObjectName: string): boolean {
    return !!(window as any)[globalObjectName];
  }

  /**
   * Private constructor - use getInstance instead
   */
  private constructor(config: SDKLoaderConfig) {
    this.config = {
      ...config,
      loadTimeout: config.loadTimeout || 30000 // 30 second default timeout
    };
  }

  /**
   * Check if SDK is loaded
   */
  isLoaded(): boolean {
    // Use static check for better performance
    const globallyLoaded = GenericSDKLoader.isGloballyLoaded(this.config.globalObjectName);
    
    if (globallyLoaded && !this.state.loaded) {
      this.state.loaded = true; // Update local state
    }
    return globallyLoaded;
  }

  /**
   * Check if SDK is currently loading
   */
  isLoading(): boolean {
    return this.state.loading;
  }

  /**
   * Get loading error if any
   */
  getError(): string | null {
    return this.state.error;
  }

  /**
   * Load SDK
   * Returns a promise that resolves when SDK is loaded
   */
  async loadSDK(): Promise<void> {
    // If already loaded, return immediately
    if (this.isLoaded()) {
      return;
    }

    // If currently loading, return existing promise
    if (this.loadPromise) {
      return this.loadPromise;
    }

    // Start loading
    this.loadPromise = this.performSDKLoad();
    return this.loadPromise;
  }

  /**
   * Perform actual SDK loading
   */
  private async performSDKLoad(): Promise<void> {
    if (this.state.loading) {
      return;
    }


    this.state.loading = true;
    this.state.error = null;

    try {
      // Create and configure script element
      const script = document.createElement('script');
      script.src = this.config.sdkUrl;
      script.async = true;
      
      // Create promise for script loading with timeout
      const loadPromise = new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          cleanup();
          reject(new Error(`SDK loading timeout after ${this.config.loadTimeout}ms`));
        }, this.config.loadTimeout);

        const cleanup = () => {
          clearTimeout(timeout);
          script.removeEventListener('load', onLoad);
          script.removeEventListener('error', onError);
        };

        const onLoad = () => {
          cleanup();
          
          // Verify SDK is available
          if ((window as any)[this.config.globalObjectName]) {
            this.state.loaded = true;
            resolve();
          } else {
            const error = `SDK loaded but ${this.config.globalObjectName} not available on window object`;
            this.state.error = error;
            reject(new Error(error));
          }
        };
        
        const onError = () => {
          cleanup();
          const error = `Failed to load SDK from ${this.config.sdkUrl}`;
          this.state.error = error;
          reject(new Error(error));
        };

        script.addEventListener('load', onLoad);
        script.addEventListener('error', onError);
      });
      
      // Add script to document
      document.head.appendChild(script);
      
      // Wait for loading to complete
      await loadPromise;
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error loading SDK';
      this.state.error = errorMessage;
      throw error;
    } finally {
      this.state.loading = false;
      this.loadPromise = null;
    }
  }

  /**
   * Reset SDK loader state
   * Useful for testing or forcing reload
   */
  reset(): void {
    this.state = {
      loaded: false,
      loading: false,
      error: null
    };
    this.loadPromise = null;
  }
}

/**
 * Convenience function to create WebRTC SDK loader
 */
export function createWebRTCSDKLoader(): GenericSDKLoader {
  return GenericSDKLoader.getInstance({
    sdkUrl: 'https://unpkg.com/amazon-kinesis-video-streams-webrtc@2.4.1/dist/kvs-webrtc.min.js',
    globalObjectName: 'KVSWebRTC'
  });
}